import { z, ZodIssueCode, isoZ } from 'my-zod'
import { dateFns } from 'iso-fns2'
import { salesQuoteSeasonsModel } from './sales-quote-seasons-model'

//////////////////////////////////////////////////////////////////////////
////////////////// RENAMED TO Vehicle Orders ON UI ///////////////////////
//////////////////////////////////////////////////////////////////////////

export const SalesQuoteStageEnum = z
  .enum([
    'Draft', // In this stage the Sales Rep will put together a vehicle order, changing the details until they are satisfied all the details are correct.
    'Negotiating', // This stage indicates the Sales Rep is actively negotiating this vehicle order with the outfitter.
    // TODO: Ideally, InternalReview generates a "Review Quote" task for Sales Manager at some point.
    'InternalReview', // The vehicle order owner can move the vehicle order from the Draft stage to Internal Review when they are ready for the Sales Manager to look over and approve the quote.
    'Approved', // Sales Manager can approve a quote.
    // TODO: Ideally, OutForSignature generates a "Check On Quote" task for Sales Manager if it has been more than "x" days
    'OutForSignature', // After approval, the vehicle order owner or Sales Manager can change its status to Out for Signature.
    // Not using right now: Upcoming: 'After a signed copy of a vehicle order is uploaded, it is Upcoming until the holding term has started.',
    // TODO: Ideally, Signed generates a "Set up Billing" task for finance team and "Order Vehicles" task for fleet team
    // TODO: Ideally we create a "Set up next year's quote" task once we reach holding term end date.
    'Signed', // Vehicle Order was signed and signed copy uploaded.  Fleet team and finance should be notified.
    'Active', // Active
    'Archived', // Archived
    'Void' // Vehicle Order owner or Sales Manager can move to Void if a vehicle order is not signed.
  ])
  .setEnumLabels({
    Draft: 'Draft',
    Negotiating: 'Negotiating',
    InternalReview: 'Internal Review',
    Approved: 'Approved',
    OutForSignature: 'Out For Signature',
    Signed: 'Signed',
    Active: 'Active',
    Archived: 'Archived',
    Void: 'Void'
  })
export const SalesQuoteStagesLabels = SalesQuoteStageEnum.enumLabels
export const QuoteStages = SalesQuoteStageEnum.Values
export type SalesQuoteStage = z.infer<typeof SalesQuoteStageEnum>

export const SalesQuoteTypeEnum = z
  .enum(['Renewal', 'SeasonExtension', 'MidSeasonAdd', 'FirstSeason', 'ProductLineAdd', 'SeasonHoldover', 'FleetSale'])
  .setEnumLabels({
    Renewal: 'Renewal',
    SeasonExtension: 'Season Extension',
    MidSeasonAdd: 'Mid-Season Add',
    FirstSeason: 'First Season',
    ProductLineAdd: 'Product Line Add',
    SeasonHoldover: 'Season Holdover',
    FleetSale: 'Fleet Sale'
  })
export type SalesQuoteType = z.infer<typeof SalesQuoteTypeEnum>

export const QuoteItemPaymentFrequencyEnum = z.enum(['OneTime', 'Monthly']).setEnumLabels({
  OneTime: 'One Time',
  Monthly: 'Monthly'
})
export const QuoteItemPaymentFrequencyOptions = Object.values(QuoteItemPaymentFrequencyEnum.Values)

export const SalesQuoteVoidReasonEnum = z
  .enum(['externalRenegotiating', 'externalDealLost', 'internalNotApproved', 'internalAdjustment', 'internalNotAFit'])
  .setEnumLabels({
    externalRenegotiating: 'External – Renegotiating',
    externalDealLost: 'External – Deal Lost',
    internalNotApproved: 'Internal – Not Approved',
    internalAdjustment: 'Internal – Adjustment',
    internalNotAFit: 'Internal – Not a fit'
  })

export type SalesQuoteVoidReason = z.infer<typeof SalesQuoteVoidReasonEnum>

export const SalesQuoteConfidenceLevelEnum = z.enum(['0%', '25%', '50%', '75%', '100%'])
export type SalesQuoteConfidenceLevelEnum = z.infer<typeof SalesQuoteConfidenceLevelEnum>

export const SalesQuoteDiscountDescriptionEnum = z
  .enum([
    'MultiProductLines',
    'MultiLocation',
    'ExecutiveDiscount',
    'ImplementationFee',
    'Loyalty',
    'SalesAllowance',
    'Other'
  ])
  .setEnumLabels({
    MultiProductLines: 'Multi Product Lines',
    MultiLocation: 'Multi Location',
    ExecutiveDiscount: 'Executive Discount',
    ImplementationFee: 'Implementation Fee',
    Loyalty: 'Loyalty',
    SalesAllowance: 'Discount',
    Other: 'Other'
  })
export const QuoteDiscountDescriptionLabels = SalesQuoteDiscountDescriptionEnum.enumLabels
export const QuoteDiscountDescriptionOptions = Object.values(SalesQuoteDiscountDescriptionEnum.Values)

export const salesQuoteDiscount = z
  .object({
    id: z.string().uuid(),
    paymentFrequency: QuoteItemPaymentFrequencyEnum.describe('Type of the discount applied to the quote.'),
    description: SalesQuoteDiscountDescriptionEnum,
    amount: z.number().describe('Discount applied to the order as a whole'),
    justification: z.string().nullish(),
    quoteHeaderId: z.number().nullish()
  })
  .superRefine((discount, ctx) => {
    if (discount.description === SalesQuoteDiscountDescriptionEnum.Enum.Other && !discount.justification?.length) {
      ctx.addIssue({
        code: ZodIssueCode.custom,
        message: `Please provide a justification for adding this discount.`,
        path: ['justification']
      })
    }
  })
export type salesQuoteDiscount = z.infer<typeof salesQuoteDiscount>

export const salesQuoteAddon = z.object({
  id: z.string().uuid(),
  paymentFrequency: QuoteItemPaymentFrequencyEnum.describe('Type of the addon added to the vehicle order.'),
  name: z.string().max(45).describe('Name of the addon added to the vehicle order.'),
  amount: z.number().describe('Addon price which will be added to the order'),
  quoteHeaderId: z.number().nullish()
})
export type salesQuoteAddon = z.infer<typeof salesQuoteAddon>

export const salesQuoteHeadersModel = z
  .object({
    id: z.number().describe('Referred to as the Vehicle Order Number.  Auto-incrementing.'),
    shortId: z.string(),
    stage: SalesQuoteStageEnum.describe('Where this vehicle order is in the overall process lifecycle.'),
    voidReason: SalesQuoteVoidReasonEnum.nullish(),
    type: SalesQuoteTypeEnum.describe('Where this vehicle order is in the overall process lifecycle.'),
    ownerId: z
      .number()
      .nullish()
      .describe('The sales representative or account manager that owns this quote. Sales Managers can change this.'),
    outfitterId: z.number().nullable().describe('Specified when the vehicle order is for a outfitter.'),
    quoteDate: isoZ.date().setLabel('Vehicle Order Date'),
    expirationDate: isoZ.date().initFn(() => dateFns.add(dateFns.now(), { days: 15 }))
      .describe(`Expiration date is the day that the vehicle order is no longer an offer. If not signed before this date, the vehicle order is void.
    Default is 15 days from vehicle order date.`),

    forecastedSignDate: isoZ
      .date()
      .nullish()
      .initFn(() => dateFns.add(dateFns.now(), { days: 5 })),
    implementationFeeAmount: z
      .number()
      .nullable()
      .describe(
        'An optional field to fill in any amount as a charge for setting up an outfitter account with Arrival by Polaris Adventures.'
      ),
    discounts: salesQuoteDiscount.array(),
    addons: salesQuoteAddon.array(),
    signedPdf: z.string().nullish(),
    signedDate: isoZ.instant().nullish(),
    notes: z.string().nullish(),
    nextSteps: z.string().nullish(),
    confidenceLevel: SalesQuoteConfidenceLevelEnum.nullish().describe(`
    25%- Interested – Interested but has not seen the pricing;
    50%- Engaged – Has viewed a vehicle order, working through follow-up questions, high likelihood of signing;
    75%- Committed – Has viewed the vehicle order, has a few logistics to work through, but has verbally committed, fleet mix and size is solidified;
  `),
    pdfDocumentTitle: z.string().nullish(),
    seasons: salesQuoteSeasonsModel.array(),
    lastModifiedDate: isoZ.instant(),
    deletedDate: isoZ.instant().nullish()
  })
  .setCFV({
    outfitterIdRequired(val) {
      if (!val.outfitterId) {
        return {
          message: 'Required',
          path: ['outfitterId']
        }
      }
    }
  })

export type salesQuoteHeadersModel = z.infer<typeof salesQuoteHeadersModel>
